import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchLevels,
  fetchStudents,
  createClassNotice,
  clearStudents,
  clearError
} from "../paperAssignmentSlice";
import { format } from 'date-fns';
import CustomCalendar from '../../worksheets/components/WorksheetModal/DateSelectionPage/CustomCalendar';
import EchoToggle from '../../../components/EchoToggle';

const ClassNoticeModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { levels, students, loading, error } = useSelector((state) => state.paperAssignment);
  
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);

  // Per-student settings: { [studentId]: { attendance, attitude, comment, classContent, homeworkItems } }
  const [studentSettings, setStudentSettings] = useState({});
  const [activeStudentId, setActiveStudentId] = useState(null);

  // Bulk apply states
  const [bulkAttendance, setBulkAttendance] = useState(null); // null = unselected, true = 출석, false = 결석
  const [bulkAttitude, setBulkAttitude] = useState(null); // null = unselected, "상", "중", "하"
  const [bulkComment, setBulkComment] = useState("");
  const [bulkClassContent, setBulkClassContent] = useState([]); // 수업 내용, 시험, 과제 정보
  const [bulkHomeworkItems, setBulkHomeworkItems] = useState([]);

  const [forceSend, setForceSend] = useState(false); // 대기열 무시하고 즉시 발송 여부
  const [classDate, setClassDate] = useState(() => new Date()); // 수업 날짜 (현재 시간)
  const [classTime, setClassTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }); // 수업 시간 (현재 시간)

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchLevels());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (selectedLevel) {
      dispatch(fetchStudents(selectedLevel));
    } else {
      dispatch(clearStudents());
    }
    setSelectedStudents([]);
  }, [selectedLevel, dispatch]);

  useEffect(() => {
    if (error) {
      alert(`오류: ${error}`);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleClose = () => {
    // Reset form
    setSelectedLevel("");
    setSelectedStudents([]);
    setStudentSettings({});
    setActiveStudentId(null);
    setBulkAttendance(null);
    setBulkAttitude(null);
    setBulkComment("");
    setBulkClassContent([]);
    setBulkHomeworkItems([]);
    setForceSend(false);
    const resetDate = new Date();
    setClassDate(resetDate);
    setClassTime(`${String(resetDate.getHours()).padStart(2, '0')}:${String(resetDate.getMinutes()).padStart(2, '0')}`);
    onClose();
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        // Remove student
        const newStudents = prev.filter(id => id !== studentId);
        // Remove from studentSettings
        setStudentSettings(settings => {
          const { [studentId]: removed, ...rest } = settings;
          return rest;
        });
        // Update activeStudentId if needed
        if (activeStudentId === studentId) {
          setActiveStudentId(newStudents.length > 0 ? newStudents[0] : null);
        }
        return newStudents;
      } else {
        // Add student
        const newStudents = [...prev, studentId];
        // Initialize student settings
        setStudentSettings(settings => ({
          ...settings,
          [studentId]: initializeStudentSettings(studentId)
        }));
        // Set as active if first student
        if (newStudents.length === 1) {
          setActiveStudentId(studentId);
        }
        return newStudents;
      }
    });
  };

  const handleSelectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      // Deselect all
      setSelectedStudents([]);
      setStudentSettings({});
      setActiveStudentId(null);
    } else {
      // Select all
      const allStudentIds = students.map(student => student.id);
      setSelectedStudents(allStudentIds);
      // Initialize settings for all students
      const newSettings = {};
      allStudentIds.forEach(id => {
        newSettings[id] = initializeStudentSettings(id);
      });
      setStudentSettings(newSettings);
      // Set first as active
      setActiveStudentId(allStudentIds[0] || null);
    }
  };

  const formatGrade = (grade) => {
    const gradeNum = parseInt(grade);
    if (gradeNum >= 1 && gradeNum <= 6) {
      return `초등 ${gradeNum}학년`;
    } else if (gradeNum >= 7 && gradeNum <= 9) {
      return `중등 ${gradeNum - 6}학년`;
    } else if (gradeNum >= 10 && gradeNum <= 12) {
      return `고등 ${gradeNum - 9}학년`;
    }
    return `${grade}학년`;
  };

  const roundScoreValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) {
      return null;
    }

    return Math.round(parsedValue * 100) / 100;
  };

  const normalizeScoreInput = (value) => {
    const roundedScore = roundScoreValue(value);
    if (roundedScore === null) {
      return '';
    }
    return roundedScore;
  };

  const hasScoreValue = (value) => value !== null && value !== undefined && value !== '';

  // Helper function: Initialize settings for a student
  const initializeStudentSettings = (studentId) => {
    return {
      attendance: null,
      attitude: null,
      comment: "",
      classContent: [],
      homeworkItems: []
    };
  };

  // Helper function: Update specific student's settings
  const updateStudentSettings = (studentId, updates) => {
    setStudentSettings(prev => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || initializeStudentSettings(studentId)),
        ...updates
      }
    }));
  };

  // Bulk Apply Handlers
  const handleBulkAttendanceChange = (attendance) => {
    setBulkAttendance(attendance);
    // Apply to all selected students
    selectedStudents.forEach(studentId => {
      updateStudentSettings(studentId, { attendance });
    });
  };

  const handleBulkAttitudeChange = (attitude) => {
    setBulkAttitude(attitude);
    // Apply to all selected students
    selectedStudents.forEach(studentId => {
      updateStudentSettings(studentId, { attitude });
    });
  };

  const handleBulkCommentChange = (comment) => {
    setBulkComment(comment);
    // Apply to all selected students
    selectedStudents.forEach(studentId => {
      updateStudentSettings(studentId, { comment });
    });
  };

  // Individual Student Handlers
  const handleStudentAttendanceChange = (studentId, attendance) => {
    updateStudentSettings(studentId, { attendance });
  };

  const handleStudentAttitudeChange = (studentId, attitude) => {
    updateStudentSettings(studentId, { attitude });
  };

  const handleStudentCommentChange = (studentId, comment) => {
    updateStudentSettings(studentId, { comment });
  };

  // Navigation Handlers
  const handleActiveStudentSelect = (studentId) => {
    if (selectedStudents.includes(parseInt(studentId))) {
      setActiveStudentId(parseInt(studentId));
    }
  };

  const goToPreviousStudent = () => {
    const currentIndex = selectedStudents.indexOf(activeStudentId);
    if (currentIndex > 0) {
      setActiveStudentId(selectedStudents[currentIndex - 1]);
    }
  };

  const goToNextStudent = () => {
    const currentIndex = selectedStudents.indexOf(activeStudentId);
    if (currentIndex < selectedStudents.length - 1) {
      setActiveStudentId(selectedStudents[currentIndex + 1]);
    }
  };

  // Bulk Class Content Handlers
  const addBulkClassContentItem = () => {
    const newItem = {
      id: Date.now(),
      type: 'lesson', // 'lesson', 'exam', 'homework'
      title: '',
      score: '',
      totalScore: 100, // 기본 총점 100점
      scoreOptional: false, // 점수 입력 선택사항 여부
      file: null, // 파일 첨부
      source: 'bulk' // 일괄 설정 항목임을 표시
    };
    setBulkClassContent([...bulkClassContent, newItem]);
    // Apply to all selected students
    selectedStudents.forEach(studentId => {
      const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
      updateStudentSettings(studentId, {
        classContent: [...currentSettings.classContent, newItem]
      });
    });
  };

  const removeBulkClassContentItem = (id) => {
    setBulkClassContent(bulkClassContent.filter(item => item.id !== id));
    // Remove from all selected students
    selectedStudents.forEach(studentId => {
      const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
      updateStudentSettings(studentId, {
        classContent: currentSettings.classContent.filter(item => item.id !== id)
      });
    });
  };

  const updateBulkClassContentItem = (id, field, value) => {
    const normalizedValue = (field === 'score' || field === 'totalScore')
      ? normalizeScoreInput(value)
      : value;

    setBulkClassContent(bulkClassContent.map(item =>
      item.id === id ? { ...item, [field]: normalizedValue } : item
    ));
    // Update for all selected students
    selectedStudents.forEach(studentId => {
      const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
      updateStudentSettings(studentId, {
        classContent: currentSettings.classContent.map(item =>
          item.id === id ? { ...item, [field]: normalizedValue } : item
        )
      });
    });
  };

  // Bulk Homework Handlers
  const addBulkHomeworkItem = () => {
    const newId = bulkHomeworkItems.length > 0
      ? Math.max(...bulkHomeworkItems.map(item => item.id)) + 1
      : 1;
    const newItem = {
      id: newId,
      file: null,
      description: "",
      hasEndDate: false,
      endDate: "",
      source: 'bulk' // 일괄 설정 항목임을 표시
    };
    setBulkHomeworkItems([...bulkHomeworkItems, newItem]);
    // Apply to all selected students
    selectedStudents.forEach(studentId => {
      const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
      updateStudentSettings(studentId, {
        homeworkItems: [...currentSettings.homeworkItems, newItem]
      });
    });
  };

  const removeBulkHomeworkItem = (itemId) => {
    setBulkHomeworkItems(items => items.filter(item => item.id !== itemId));
    // Remove from all selected students
    selectedStudents.forEach(studentId => {
      const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
      updateStudentSettings(studentId, {
        homeworkItems: currentSettings.homeworkItems.filter(item => item.id !== itemId)
      });
    });
  };

  const updateBulkHomeworkItem = (itemId, field, value) => {
    setBulkHomeworkItems(items =>
      items.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
    // Update for all selected students
    selectedStudents.forEach(studentId => {
      const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
      updateStudentSettings(studentId, {
        homeworkItems: currentSettings.homeworkItems.map(item =>
          item.id === itemId ? { ...item, [field]: value } : item
        )
      });
    });
  };

  // Individual Student Class Content Handlers
  const updateStudentClassContentItem = (studentId, id, field, value) => {
    const normalizedValue = (field === 'score' || field === 'totalScore')
      ? normalizeScoreInput(value)
      : value;

    const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
    updateStudentSettings(studentId, {
      classContent: currentSettings.classContent.map(item =>
        item.id === id ? { ...item, [field]: normalizedValue } : item
      )
    });
  };

  // Individual Student Homework Handlers
  const updateStudentHomeworkItem = (studentId, itemId, field, value) => {
    const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
    updateStudentSettings(studentId, {
      homeworkItems: currentSettings.homeworkItems.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    });
  };

  // Individual Student Class Content Management (only for active student)
  const addStudentClassContentItem = (studentId) => {
    const newItem = {
      id: Date.now(),
      type: 'lesson',
      title: '',
      score: '',
      totalScore: 100,
      scoreOptional: false,
      file: null,
      source: 'individual' // 개별 설정 항목임을 표시
    };
    const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
    updateStudentSettings(studentId, {
      classContent: [...currentSettings.classContent, newItem]
    });
  };

  const removeStudentClassContentItem = (studentId, itemId) => {
    const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
    const itemToRemove = currentSettings.classContent.find(item => item.id === itemId);

    // Safety check: only allow removal of individual items
    if (itemToRemove && itemToRemove.source === 'individual') {
      updateStudentSettings(studentId, {
        classContent: currentSettings.classContent.filter(item => item.id !== itemId)
      });
    }
  };

  // Individual Student Homework Management (only for active student)
  const addStudentHomeworkItem = (studentId) => {
    const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
    const newId = currentSettings.homeworkItems.length > 0
      ? Math.max(...currentSettings.homeworkItems.map(item => item.id)) + 1
      : 1;

    const newItem = {
      id: newId,
      file: null,
      description: "",
      hasEndDate: false,
      endDate: "",
      source: 'individual' // 개별 설정 항목임을 표시
    };

    updateStudentSettings(studentId, {
      homeworkItems: [...currentSettings.homeworkItems, newItem]
    });
  };

  const removeStudentHomeworkItem = (studentId, itemId) => {
    const currentSettings = studentSettings[studentId] || initializeStudentSettings(studentId);
    const itemToRemove = currentSettings.homeworkItems.find(item => item.id === itemId);

    // Safety check: only allow removal of individual items
    if (itemToRemove && itemToRemove.source === 'individual') {
      updateStudentSettings(studentId, {
        homeworkItems: currentSettings.homeworkItems.filter(item => item.id !== itemId)
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedLevel || selectedStudents.length === 0) {
      alert("클래스와 최소 한 명 이상의 학생을 선택해주세요");
      return;
    }

    // Create separate FormData for each selected student using their individual settings
    const promises = selectedStudents.map(async (studentId) => {
      const settings = studentSettings[studentId] || initializeStudentSettings(studentId);
      const formData = new FormData();
      formData.append('level_id', selectedLevel);

      // Find and append level_name for notification template
      const selectedLevelObj = levels.find(level => level.id === parseInt(selectedLevel));
      if (selectedLevelObj) {
        formData.append('level_name', selectedLevelObj.name);
      }

      formData.append('student_id', studentId);

      // Always append attendance and attitude (even if null) - use individual student settings
      formData.append('attendance', settings.attendance);
      formData.append('attitude', settings.attitude);

      formData.append('comment', settings.comment);

      // Merge bulk class content with individual student's class content
      // Bulk items (일괄 설정) apply to all students, individual items are student-specific
      const allClassContent = [
        ...bulkClassContent.filter(item => item.source === 'bulk'),
        ...settings.classContent.filter(item => item.source === 'individual')
      ];

      // Prepare class_content without file objects for JSON
      const classContentForJSON = allClassContent.map(item => ({
        id: item.id,
        type: item.type,
        title: item.title,
        score: item.scoreOptional ? null : (hasScoreValue(item.score) ? roundScoreValue(item.score) : null),
        totalScore: item.scoreOptional ? null : (hasScoreValue(item.totalScore) ? roundScoreValue(item.totalScore) : null)
      }));
      formData.append('class_content', JSON.stringify(classContentForJSON));

      // Append class_content files separately
      allClassContent.forEach(item => {
        if (item.file) {
          formData.append('class_content_files', item.file);
        }
      });

      // Merge bulk homework items with individual student's homework items
      // Bulk items (일괄 설정) apply to all students, individual items are student-specific
      const allHomeworkItems = [
        ...bulkHomeworkItems.filter(item => item.source === 'bulk'),
        ...settings.homeworkItems.filter(item => item.source === 'individual')
      ];

      // Append homework files and titles (files are optional)
      allHomeworkItems.forEach(item => {
        // Allow homework items with just a title (no file required)
        if (item.file || (item.description && item.description.trim())) {
          if (item.file) {
            formData.append('homework_files', item.file);
          }
          formData.append('homework_titles', item.description || item.file?.name || '제목 없음');

          // Append end_date for each homework item
          // Send only date (YYYY-MM-DD), server will set time to 23:59:59 in local timezone
          let endDateValue = '';
          if (item.hasEndDate && item.endDate) {
            // Send date only (no time) to avoid timezone conversion issues
            const date = new Date(item.endDate);
            endDateValue = date.toISOString().split('T')[0];  // "YYYY-MM-DD"
          }
          formData.append('homework_end_dates', endDateValue);
        }
      });

      // Append class_date in ISO format with time (local time converted to UTC)
      // classDate is in local time (KST), toISOString() converts it to UTC
      const formattedDate = classDate.toISOString().slice(0, 19); // YYYY-MM-DDTHH:mm:ss in UTC
      formData.append('class_date', formattedDate);

      // Append force_send flag
      formData.append('force_send', forceSend);

      return dispatch(createClassNotice(formData));
    });

    try {
      const results = await Promise.all(promises);
      const allSuccessful = results.every(result => createClassNotice.fulfilled.match(result));

      if (allSuccessful) {
        alert(`${selectedStudents.length}명의 학생에게 알림장이 성공적으로 발송되었습니다!`);
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      }
    } catch (error) {
      console.error('Error creating class notices:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#FFF',
        borderRadius: '10px',
        padding: '30px',
        width: '800px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '25px'
        }}>
          <h2 style={{ 
            margin: 0, 
            color: '#3A434E', 
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            알림장 및 과제 발송
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#B9C1C9'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Level and Student Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#3A434E' }}>
              클래스 선택 *
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              required
              style={{ 
                width: '100%',
                padding: '12px', 
                border: '1px solid #E6EDEC', 
                borderRadius: '5px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            >
              <option value="">클래스를 선택하세요</option>
              {levels.map(level => (
                <option key={level.id} value={level.id}>{level.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontWeight: '500', color: '#3A434E' }}>
                학생 선택 * ({selectedStudents.length}명 선택됨)
              </label>
              {students.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAllStudents}
                  style={{
                    padding: '4px 12px',
                    fontSize: '13px',
                    borderRadius: '5px',
                    backgroundColor: selectedStudents.length === students.length ? '#7A8490' : '#0E141B',
                    border: 'none',
                    color: '#FFF',
                    cursor: 'pointer'
                  }}
                >
                  {selectedStudents.length === students.length ? '전체 해제' : '전체 선택'}
                </button>
              )}
            </div>
            
            {!selectedLevel ? (
              <div style={{ 
                padding: '20px', 
                border: '1px dashed #E6EDEC', 
                borderRadius: '5px',
                textAlign: 'center',
                color: '#B9C1C9',
                backgroundColor: '#F4F8F7'
              }}>
                먼저 클래스를 선택해주세요
              </div>
            ) : students.length === 0 ? (
              <div style={{ 
                padding: '20px', 
                border: '1px dashed #E6EDEC', 
                borderRadius: '5px',
                textAlign: 'center',
                color: '#B9C1C9'
              }}>
                이 클래스에 등록된 학생이 없습니다
              </div>
            ) : (
              <div style={{ 
                border: '1px solid #E6EDEC', 
                borderRadius: '5px',
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: '#FFF'
              }}>
                {students.map(student => (
                  <div 
                    key={student.id}
                    onClick={() => handleStudentToggle(student.id)}
                    style={{ 
                      padding: '10px 15px',
                      borderBottom: '1px solid #E6EDEC',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      backgroundColor: selectedStudents.includes(student.id) ? '#e8f4ff' : '#FFF',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedStudents.includes(student.id)) {
                        e.currentTarget.style.backgroundColor = '#F4F8F7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedStudents.includes(student.id)) {
                        e.currentTarget.style.backgroundColor = '#FFF';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => {}}
                      style={{ 
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ 
                      flex: 1,
                      fontSize: '15px',
                      color: selectedStudents.includes(student.id) ? '#0E141B' : '#3A434E',
                      fontWeight: selectedStudents.includes(student.id) ? '500' : 'normal'
                    }}>
                      {student.name}
                    </span>
                    <span style={{ 
                      fontSize: '14px', 
                      color: '#7A8490',
                      backgroundColor: '#f0f0f0',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {formatGrade(student.grade)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Class Date Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '7px', fontWeight: 800, color: '#7A8490', fontSize: '0.72rem', letterSpacing: '0.06em' }}>
              수업 날짜
            </label>
            <div style={{
              border: '1px solid #E6EDEC',
              borderRadius: '14px',
              overflow: 'hidden',
              backgroundColor: '#FFF'
            }}>
              <CustomCalendar
                selectedDate={classDate}
                handleDateSelect={(date) => {
                  const [hours, minutes] = classTime.split(':');
                  const next = new Date(date);
                  next.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                  setClassDate(next);
                }}
              />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
                padding: '12px 16px',
                backgroundColor: '#F4F8F7',
                borderTop: '1px solid #E6EDEC'
              }}>
                <div style={{ flex: 1, minWidth: '140px' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', color: '#7A8490' }}>
                    선택된 날짜
                  </div>
                  <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0E141B', marginTop: '2px' }}>
                    {format(classDate, 'yyyy년 MM월 dd일')}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', color: '#7A8490', marginBottom: '4px' }}>
                    수업 시간
                  </div>
                  <input
                    type="time"
                    value={classTime}
                    onChange={(e) => {
                      setClassTime(e.target.value);
                      const [hours, minutes] = e.target.value.split(':');
                      const newDate = new Date(classDate);
                      newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                      setClassDate(newDate);
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1.5px solid #E6EDEC',
                      borderRadius: '10px',
                      fontSize: '0.88rem',
                      fontWeight: 700,
                      color: '#0E141B',
                      backgroundColor: '#FFF',
                      cursor: 'pointer',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bulk Settings Section */}
          {selectedStudents.length > 0 && (
            <div style={{
              marginBottom: '30px',
              padding: '18px',
              border: '1px solid #E6EDEC',
              borderRadius: '14px',
              backgroundColor: '#FFF'
            }}>
              <h3 style={{
                margin: '0 0 6px 0',
                color: '#0E141B',
                fontSize: '1rem',
                fontWeight: 800,
                letterSpacing: '-0.01em'
              }}>
                전체 설정 (일괄 적용)
              </h3>
              <p style={{
                margin: '0 0 20px 0',
                fontSize: '14px',
                color: '#7A8490'
              }}>
                선택된 {selectedStudents.length}명의 학생에게 동일한 설정을 적용합니다.
              </p>

              {/* Bulk Attendance */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '7px', fontWeight: 800, color: '#7A8490', fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                  출결
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' }}>
                  <button
                    type="button"
                    onClick={() => handleBulkAttendanceChange(null)}
                style={{
                  padding: '9px 4px',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 700,
                  borderRadius: '999px',
                  backgroundColor: bulkAttendance === null ? '#0E141B' : '#FFF',
                  border: bulkAttendance === null ? '1.5px solid #0E141B' : '1.5px solid #E6EDEC',
                  color: bulkAttendance === null ? '#FFF' : '#3A434E',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                선택 안 함
              </button>
              <button
                type="button"
                onClick={() => handleBulkAttendanceChange(true)}
                style={{
                  padding: '9px 4px',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 700,
                  borderRadius: '999px',
                  backgroundColor: bulkAttendance === true ? '#0E141B' : '#FFF',
                  border: bulkAttendance === true ? '1.5px solid #0E141B' : '1.5px solid #E6EDEC',
                  color: bulkAttendance === true ? '#FFF' : '#3A434E',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                모두 출석
              </button>
              <button
                type="button"
                onClick={() => handleBulkAttendanceChange(false)}
                style={{
                  padding: '9px 4px',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 700,
                  borderRadius: '999px',
                  backgroundColor: bulkAttendance === false ? '#0E141B' : '#FFF',
                  border: bulkAttendance === false ? '1.5px solid #0E141B' : '1.5px solid #E6EDEC',
                  color: bulkAttendance === false ? '#FFF' : '#3A434E',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                모두 결석
              </button>
            </div>
          </div>

          {/* Bulk Attitude */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '7px', fontWeight: 800, color: '#7A8490', fontSize: '0.72rem', letterSpacing: '0.06em' }}>
              수업 태도
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
              <button
                type="button"
                onClick={() => handleBulkAttitudeChange(null)}
                style={{
                  padding: '9px 4px',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 700,
                  borderRadius: '999px',
                  backgroundColor: bulkAttitude === null ? '#0E141B' : '#FFF',
                  border: bulkAttitude === null ? '1.5px solid #0E141B' : '1.5px solid #E6EDEC',
                  color: bulkAttitude === null ? '#FFF' : '#3A434E',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                선택 안 함
              </button>
              <button
                type="button"
                onClick={() => handleBulkAttitudeChange('상')}
                style={{
                  padding: '9px 4px',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 700,
                  borderRadius: '999px',
                  backgroundColor: bulkAttitude === '상' ? '#0E141B' : '#FFF',
                  border: bulkAttitude === '상' ? '1.5px solid #0E141B' : '1.5px solid #E6EDEC',
                  color: bulkAttitude === '상' ? '#FFF' : '#3A434E',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                상
              </button>
              <button
                type="button"
                onClick={() => handleBulkAttitudeChange('중')}
                style={{
                  padding: '9px 4px',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 700,
                  borderRadius: '999px',
                  backgroundColor: bulkAttitude === '중' ? '#0E141B' : '#FFF',
                  border: bulkAttitude === '중' ? '1.5px solid #0E141B' : '1.5px solid #E6EDEC',
                  color: bulkAttitude === '중' ? '#FFF' : '#3A434E',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                중
              </button>
              <button
                type="button"
                onClick={() => handleBulkAttitudeChange('하')}
                style={{
                  padding: '9px 4px',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 700,
                  borderRadius: '999px',
                  backgroundColor: bulkAttitude === '하' ? '#0E141B' : '#FFF',
                  border: bulkAttitude === '하' ? '1.5px solid #0E141B' : '1.5px solid #E6EDEC',
                  color: bulkAttitude === '하' ? '#FFF' : '#3A434E',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                하
              </button>
            </div>
          </div>

          {/* Bulk Comment */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#3A434E' }}>
              코멘트 일괄 적용
            </label>
            <textarea
              value={bulkComment}
              onChange={(e) => handleBulkCommentChange(e.target.value)}
              placeholder="전체 학생에게 적용할 코멘트를 입력하세요"
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #E6EDEC',
                borderRadius: '5px',
                fontSize: '16px',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Bulk Class Content */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0E141B', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                  수업 내용
                </div>
                <div style={{ fontSize: '0.7rem', color: '#7A8490', marginTop: '1px' }}>
                  시험·과제 결과
                </div>
              </div>
              <button
                type="button"
                onClick={addBulkClassContentItem}
                style={{
                  padding: '7px 14px',
                  fontSize: '0.76rem',
                  backgroundColor: '#FFF',
                  color: '#3A434E',
                  border: '1.5px solid #E6EDEC',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'border-color 0.12s, color 0.12s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#00D9C0';
                  e.currentTarget.style.color = '#00A396';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E6EDEC';
                  e.currentTarget.style.color = '#3A434E';
                }}
              >
                + 추가
              </button>
            </div>
            
            {bulkClassContent.length === 0 ? (
              <div style={{
                padding: '18px 12px',
                textAlign: 'center',
                border: '1.5px dashed #E6EDEC',
                borderRadius: '12px',
                backgroundColor: '#FFF',
                color: '#B9C1C9',
                fontSize: '0.78rem'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}></div>
                <div style={{ fontSize: '14px' }}>수업 내용이나 시험/과제 결과를 추가해보세요</div>
              </div>
            ) : (
              <div style={{
                border: '1px solid #E6EDEC',
                borderRadius: '8px',
                backgroundColor: '#fff',
                overflow: 'hidden'
              }}>
                {bulkClassContent.map((item, index) => (
                  <div key={item.id} style={{
                    padding: '16px',
                    borderBottom: index < bulkClassContent.length - 1 ? '1px solid #f1f3f4' : 'none',
                    backgroundColor: '#fafbfc'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                      <div style={{
                        minWidth: '24px',
                        height: '24px',
                        backgroundColor: item.type === 'lesson' ? '#00A396' :
                                       item.type === 'exam' ? '#C2452D' : '#00D9C0',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {item.type === 'lesson' ? '' : item.type === 'exam' ? '' : ''}
                      </div>

                      <select
                        value={item.type}
                        onChange={(e) => updateBulkClassContentItem(item.id, 'type', e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #E6EDEC',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          backgroundColor: '#fff',
                          color: '#3A434E',
                          minWidth: '80px'
                        }}
                      >
                        <option value="lesson">수업</option>
                        <option value="exam">시험</option>
                        <option value="homework">과제</option>
                      </select>

                      <input
                        type="text"
                        placeholder="제목을 입력하세요"
                        value={item.title}
                        onChange={(e) => updateBulkClassContentItem(item.id, 'title', e.target.value)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #E6EDEC',
                          borderRadius: '6px',
                          fontSize: '14px',
                          backgroundColor: '#fff'
                        }}
                      />

                      <button
                        type="button"
                        onClick={() => removeBulkClassContentItem(item.id)}
                        style={{
                          padding: '6px 10px',
                          fontSize: '12px',
                          backgroundColor: '#C2452D',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                        title="삭제"
                      >
                        삭제
                      </button>
                    </div>
                    
                    {(item.type === 'exam' || item.type === 'homework') && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        marginLeft: '36px',
                        padding: '12px',
                        backgroundColor: '#fff',
                        border: '1px solid #E6EDEC',
                        borderRadius: '6px'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={item.scoreOptional || false}
                            onChange={(e) => updateBulkClassContentItem(item.id, 'scoreOptional', e.target.checked)}
                            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                          />
                          <span style={{ fontSize: '13px', color: '#3A434E' }}>점수 입력하지 않기</span>
                        </label>

                        {!item.scoreOptional && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <label style={{ fontSize: '14px', color: '#3A434E', fontWeight: '500', minWidth: '40px' }}>
                              점수:
                            </label>
                            <input
                              type="number"
                              placeholder="0"
                              step="0.01"
                              value={item.score}
                              onChange={(e) => updateBulkClassContentItem(item.id, 'score', e.target.value)}
                              style={{
                                width: '70px',
                                padding: '6px 10px',
                                border: '1px solid #E6EDEC',
                                borderRadius: '4px',
                                fontSize: '14px',
                                textAlign: 'center'
                              }}
                            />
                            <span style={{ color: '#7A8490', fontSize: '14px' }}>/</span>
                            <input
                              type="number"
                              placeholder="100"
                              step="0.01"
                              value={item.totalScore ?? ''}
                              onChange={(e) => updateBulkClassContentItem(item.id, 'totalScore', e.target.value)}
                              style={{
                                width: '70px',
                                padding: '6px 10px',
                                border: '1px solid #E6EDEC',
                                borderRadius: '4px',
                                fontSize: '14px',
                                textAlign: 'center'
                              }}
                            />
                            <span style={{ color: '#7A8490', fontSize: '14px' }}>점</span>

                            {hasScoreValue(item.score) && hasScoreValue(item.totalScore) && (
                              <div style={{
                                marginLeft: '12px',
                                padding: '4px 8px',
                                backgroundColor: parseFloat(item.score) / parseFloat(item.totalScore) >= 0.8 ? '#E5FBF8' :
                                               parseFloat(item.score) / parseFloat(item.totalScore) >= 0.6 ? '#FBF4D9' : '#FCEDE9',
                                color: parseFloat(item.score) / parseFloat(item.totalScore) >= 0.8 ? '#00806B' :
                                      parseFloat(item.score) / parseFloat(item.totalScore) >= 0.6 ? '#8A6D00' : '#A93A25',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {Math.round((parseFloat(item.score) / parseFloat(item.totalScore)) * 100)}%
                              </div>
                            )}
                          </div>
                        )}

                        {item.scoreOptional && (
                          <span style={{ fontSize: '12px', color: '#7A8490', fontStyle: 'italic' }}>
                            점수를 입력하지 않습니다
                          </span>
                        )}
                      </div>
                    )}

                    {/* 파일 첨부 */}
                    <div style={{
                      marginLeft: '36px',
                      marginTop: '10px',
                      padding: '12px',
                      backgroundColor: '#fff',
                      border: '1px solid #E6EDEC',
                      borderRadius: '6px'
                    }}>
                      <label style={{ fontSize: '14px', color: '#3A434E', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                         파일 첨부 (선택):
                      </label>
                      <input
                        type="file"
                        onChange={(e) => updateBulkClassContentItem(item.id, 'file', e.target.files[0])}
                        style={{
                          fontSize: '13px',
                          padding: '6px',
                          border: '1px solid #E6EDEC',
                          borderRadius: '4px',
                          width: '100%',
                          backgroundColor: '#F4F8F7'
                        }}
                      />
                      {item.file && (
                        <div style={{ marginTop: '6px', fontSize: '12px', color: '#00D9C0' }}>
                           {item.file.name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bulk Homework */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0E141B', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                  과제
                </div>
                <div style={{ fontSize: '0.7rem', color: '#7A8490', marginTop: '1px' }}>
                  학생에게 나갈 숙제
                </div>
              </div>
              <button
                type="button"
                onClick={addBulkHomeworkItem}
                style={{
                  padding: '7px 14px',
                  fontSize: '0.76rem',
                  borderRadius: '999px',
                  backgroundColor: '#FFF',
                  border: '1.5px solid #E6EDEC',
                  color: '#3A434E',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'border-color 0.12s, color 0.12s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#00D9C0';
                  e.currentTarget.style.color = '#00A396';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E6EDEC';
                  e.currentTarget.style.color = '#3A434E';
                }}
              >
                + 추가
              </button>
            </div>
            
            {bulkHomeworkItems.length === 0 ? (
              <div style={{
                padding: '18px 12px',
                border: '1.5px dashed #E6EDEC',
                borderRadius: '12px',
                textAlign: 'center',
                color: '#B9C1C9',
                fontSize: '0.78rem'
              }}>
                아직 등록된 과제가 없어요
              </div>
            ) : (
              bulkHomeworkItems.map((item, index) => (
              <div key={item.id} style={{
                marginBottom: '15px',
                padding: '15px',
                border: '1px solid #E6EDEC',
                borderRadius: '5px',
                backgroundColor: '#f9f9f9'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: '500', color: '#3A434E' }}>과제 {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeBulkHomeworkItem(item.id)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      borderRadius: '3px',
                      backgroundColor: '#C2452D',
                      border: 'none',
                      color: '#FFF',
                      cursor: 'pointer'
                    }}
                  >
                    삭제
                  </button>
                </div>
                
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#7A8490' }}>
                    과제 제목
                  </label>
                  <input
                    type="text"
                    placeholder="과제 제목을 입력하세요"
                    value={item.description}
                    onChange={(e) => updateBulkHomeworkItem(item.id, 'description', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #E6EDEC',
                      borderRadius: '5px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      backgroundColor: '#FFF'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#7A8490' }}>
                    과제 파일 선택 <span style={{ fontSize: '12px', color: '#B9C1C9' }}>(선택사항)</span>
                  </label>
                  <input
                    type="file"
                    onChange={(e) => updateBulkHomeworkItem(item.id, 'file', e.target.files[0])}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #E6EDEC',
                      borderRadius: '5px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      backgroundColor: '#FFF'
                    }}
                  />
                  {item.file && (
                    <div style={{ marginTop: '5px', color: '#7A8490', fontSize: '12px' }}>
                      선택된 파일: {item.file.name}
                    </div>
                  )}
                </div>

                {/* END DATE Section */}
                <div style={{
                  marginTop: '10px',
                  padding: '12px',
                  backgroundColor: '#F4F8F7',
                  border: '1px solid #E5FBF8',
                  borderRadius: '5px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="checkbox"
                      checked={item.hasEndDate || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateBulkHomeworkItem(item.id, 'hasEndDate', checked);

                        // When enabling deadline, set default to +3 days from today
                        if (checked && !item.endDate) {
                          const defaultDate = new Date();
                          defaultDate.setDate(defaultDate.getDate() + 3);

                          // Format for date input: YYYY-MM-DD
                          const formatted = defaultDate.toISOString().slice(0, 10);
                          updateBulkHomeworkItem(item.id, 'endDate', formatted);
                        }
                      }}
                      style={{
                        marginRight: '8px',
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px'
                      }}
                    />
                    <span style={{ fontSize: '14px', color: '#3A434E', fontWeight: '500' }}>
                      제출 마감일 설정
                    </span>
                  </label>

                  {item.hasEndDate && (
                    <div style={{ marginTop: '8px' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '5px',
                        fontSize: '13px',
                        color: '#7A8490'
                      }}>
                        마감일시
                      </label>
                      <input
                        type="date"
                        value={item.endDate || ''}
                        onChange={(e) => updateBulkHomeworkItem(item.id, 'endDate', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #E5FBF8',
                          borderRadius: '5px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          backgroundColor: '#FFF'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))
            )}
          </div>
        </div>
          )}

          {/* Individual Student Settings Section */}
          {selectedStudents.length > 0 && activeStudentId && (
            <div style={{
              marginBottom: '30px',
              border: '1px solid #E6EDEC',
              borderRadius: '14px',
              overflow: 'hidden',
              backgroundColor: '#FFF'
            }}>
              <div style={{
                padding: '11px 14px',
                backgroundColor: '#F4F8F7',
                fontSize: '0.8rem',
                fontWeight: 800,
                color: '#0E141B',
                letterSpacing: '-0.01em'
              }}>
                개별 학생 설정
              </div>
              <div style={{ padding: '14px' }}>

              {/* Student Selector */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '7px', fontWeight: 800, color: '#7A8490', fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                  학생 선택{' '}
                  <span style={{ color: '#00A396' }}>
                    {selectedStudents.indexOf(activeStudentId) + 1} / {selectedStudents.length}
                  </span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={goToPreviousStudent}
                    disabled={selectedStudents.indexOf(activeStudentId) === 0}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #B9C1C9',
                      backgroundColor: selectedStudents.indexOf(activeStudentId) === 0 ? '#F4F8F7' : '#fff',
                      color: '#4b5563',
                      cursor: selectedStudents.indexOf(activeStudentId) === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    이전
                  </button>
                  <select
                    value={activeStudentId}
                    onChange={(e) => handleActiveStudentSelect(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #B9C1C9',
                      fontSize: '14px',
                      color: '#0E141B',
                      outline: 'none',
                      backgroundColor: '#fff'
                    }}
                  >
                    {selectedStudents.map(studentId => {
                      const student = students.find(s => s.id === studentId);
                      return (
                        <option key={studentId} value={studentId}>
                          {student ? `${student.name} (${formatGrade(student.grade)})` : `학생 ${studentId}`}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    onClick={goToNextStudent}
                    disabled={selectedStudents.indexOf(activeStudentId) === selectedStudents.length - 1}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #B9C1C9',
                      backgroundColor: selectedStudents.indexOf(activeStudentId) === selectedStudents.length - 1 ? '#F4F8F7' : '#fff',
                      color: '#4b5563',
                      cursor: selectedStudents.indexOf(activeStudentId) === selectedStudents.length - 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    다음
                  </button>
                </div>
              </div>

              {(() => {
                const settings = studentSettings[activeStudentId] || initializeStudentSettings(activeStudentId);
                return (
                  <>
                    {/* Individual Attendance */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#3A434E' }}>
                        출결
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' }}>
                        <button
                          type="button"
                          onClick={() => handleStudentAttendanceChange(activeStudentId, null)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '6px',
                            border: `2px solid ${settings.attendance === null ? '#7A8490' : '#E6EDEC'}`,
                            backgroundColor: settings.attendance === null ? '#7A8490' : '#FFF',
                            color: settings.attendance === null ? '#FFF' : '#7A8490',
                            cursor: 'pointer'
                          }}
                        >
                          선택 안 함
                        </button>
                        <button
                          type="button"
                          onClick={() => handleStudentAttendanceChange(activeStudentId, true)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '6px',
                            border: `2px solid ${settings.attendance === true ? '#00D9C0' : '#E6EDEC'}`,
                            backgroundColor: settings.attendance === true ? '#00D9C0' : '#FFF',
                            color: settings.attendance === true ? '#FFF' : '#7A8490',
                            cursor: 'pointer'
                          }}
                        >
                          출석
                        </button>
                        <button
                          type="button"
                          onClick={() => handleStudentAttendanceChange(activeStudentId, false)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '6px',
                            border: `2px solid ${settings.attendance === false ? '#C2452D' : '#E6EDEC'}`,
                            backgroundColor: settings.attendance === false ? '#C2452D' : '#FFF',
                            color: settings.attendance === false ? '#FFF' : '#7A8490',
                            cursor: 'pointer'
                          }}
                        >
                          결석
                        </button>
                      </div>
                    </div>

                    {/* Individual Attitude */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#3A434E' }}>
                        수업 태도
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' }}>
                        {[
                          { label: '선택 안 함', value: null },
                          { label: '상', value: '상' },
                          { label: '중', value: '중' },
                          { label: '하', value: '하' }
                        ].map(option => (
                          <button
                            key={option.label}
                            type="button"
                            onClick={() => handleStudentAttitudeChange(activeStudentId, option.value)}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '6px',
                              border: `2px solid ${settings.attitude === option.value ? '#0E141B' : '#E6EDEC'}`,
                              backgroundColor: settings.attitude === option.value ? '#0E141B' : '#FFF',
                              color: settings.attitude === option.value ? '#FFF' : '#7A8490',
                              cursor: 'pointer'
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Individual Comment */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#3A434E' }}>
                        코멘트
                      </label>
                      <textarea
                        value={settings.comment}
                        onChange={(e) => handleStudentCommentChange(activeStudentId, e.target.value)}
                        placeholder="개별 코멘트를 입력하세요"
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #E6EDEC',
                          borderRadius: '5px',
                          fontSize: '16px',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    {/* Individual Class Content - Editable UI */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <label style={{ fontWeight: '600', color: '#3A434E', fontSize: '16px' }}>
                           수업 내용 ({settings.classContent.length}개)
                        </label>
                        <button
                          type="button"
                          onClick={() => addStudentClassContentItem(activeStudentId)}
                          style={{
                            padding: '8px 16px',
                            fontSize: '14px',
                            backgroundColor: '#0E141B',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            boxShadow: '0 2px 4px rgba(0,123,255,0.2)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                           항목 추가 (개별)
                        </button>
                      </div>

                      {settings.classContent.length === 0 ? (
                        <div style={{
                          padding: '30px',
                          textAlign: 'center',
                          border: '2px dashed #E6EDEC',
                          borderRadius: '8px',
                          backgroundColor: '#F4F8F7',
                          color: '#7A8490'
                        }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}></div>
                          <div style={{ fontSize: '14px' }}>
                            일괄 설정에서 추가하거나, 위의 "항목 추가" 버튼으로 이 학생만의 수업 내용을 추가하세요
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          border: '1px solid #E6EDEC',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          overflow: 'hidden'
                        }}>
                          {settings.classContent.map((item, index) => (
                            <div key={item.id} style={{
                              padding: '16px',
                              borderBottom: index < settings.classContent.length - 1 ? '1px solid #f1f3f4' : 'none',
                              backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#fafbfc'
                            }}>
                              {/* Source badge */}
                              {item.source === 'bulk' && (
                                <div style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  backgroundColor: '#E5FBF8',
                                  color: '#1976d2',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  marginBottom: '8px'
                                }}>
                                  일괄 설정
                                </div>
                              )}
                              {item.source === 'individual' && (
                                <div style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  backgroundColor: '#e8f5e9',
                                  color: '#388e3c',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  marginBottom: '8px'
                                }}>
                                  개별 설정
                                </div>
                              )}

                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                <div style={{
                                  minWidth: '24px',
                                  height: '24px',
                                  backgroundColor: item.type === 'lesson' ? '#00A396' :
                                                 item.type === 'exam' ? '#C2452D' : '#00D9C0',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  {item.type === 'lesson' ? '' : item.type === 'exam' ? '' : ''}
                                </div>

                                <select
                                  value={item.type}
                                  onChange={(e) => updateStudentClassContentItem(activeStudentId, item.id, 'type', e.target.value)}
                                  disabled={item.source === 'bulk'}
                                  style={{
                                    padding: '8px 12px',
                                    border: '1px solid #E6EDEC',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#fff',
                                    color: '#3A434E',
                                    minWidth: '80px',
                                    cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  <option value="lesson">수업</option>
                                  <option value="exam">시험</option>
                                  <option value="homework">과제</option>
                                </select>

                                <input
                                  type="text"
                                  placeholder="제목을 입력하세요"
                                  value={item.title}
                                  onChange={(e) => updateStudentClassContentItem(activeStudentId, item.id, 'title', e.target.value)}
                                  disabled={item.source === 'bulk'}
                                  style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    border: '1px solid #E6EDEC',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#fff',
                                    cursor: item.source === 'bulk' ? 'not-allowed' : 'text'
                                  }}
                                />

                                {item.source === 'individual' && (
                                  <button
                                    type="button"
                                    onClick={() => removeStudentClassContentItem(activeStudentId, item.id)}
                                    style={{
                                      padding: '6px 10px',
                                      fontSize: '12px',
                                      backgroundColor: '#C2452D',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                    title="삭제"
                                  >
                                    삭제
                                  </button>
                                )}
                              </div>

                              {/* Score section */}
                              {(item.type === 'exam' || item.type === 'homework') && (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '8px',
                                  marginLeft: '36px',
                                  padding: '12px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #E6EDEC',
                                  borderRadius: '6px'
                                }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={item.scoreOptional || false}
                                      onChange={(e) => updateStudentClassContentItem(activeStudentId, item.id, 'scoreOptional', e.target.checked)}
                                      disabled={item.source === 'bulk'}
                                      style={{ cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer', width: '14px', height: '14px' }}
                                    />
                                    <span style={{ fontSize: '13px', color: '#3A434E' }}>점수 입력하지 않기</span>
                                  </label>

                                  {!item.scoreOptional && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <label style={{ fontSize: '14px', color: '#3A434E', fontWeight: '500', minWidth: '40px' }}>
                                        점수:
                                      </label>
                                      <input
                                        type="number"
                                        placeholder="0"
                                        step="0.01"
                                        value={item.score}
                                        onChange={(e) => updateStudentClassContentItem(activeStudentId, item.id, 'score', e.target.value)}
                                        disabled={item.source === 'bulk'}
                                        style={{
                                          width: '70px',
                                          padding: '6px 10px',
                                          border: '1px solid #E6EDEC',
                                          borderRadius: '4px',
                                          fontSize: '14px',
                                          textAlign: 'center',
                                          backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#fff',
                                          cursor: item.source === 'bulk' ? 'not-allowed' : 'text'
                                        }}
                                      />
                                      <span style={{ color: '#7A8490', fontSize: '14px' }}>/</span>
                                      <input
                                        type="number"
                                        placeholder="100"
                                        step="0.01"
                                        value={item.totalScore ?? ''}
                                        onChange={(e) => updateStudentClassContentItem(activeStudentId, item.id, 'totalScore', e.target.value)}
                                        disabled={item.source === 'bulk'}
                                        style={{
                                          width: '70px',
                                          padding: '6px 10px',
                                          border: '1px solid #E6EDEC',
                                          borderRadius: '4px',
                                          fontSize: '14px',
                                          textAlign: 'center',
                                          backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#fff',
                                          cursor: item.source === 'bulk' ? 'not-allowed' : 'text'
                                        }}
                                      />
                                      <span style={{ color: '#7A8490', fontSize: '14px' }}>점</span>

                                      {hasScoreValue(item.score) && hasScoreValue(item.totalScore) && (
                                        <div style={{
                                          marginLeft: '12px',
                                          padding: '4px 8px',
                                          backgroundColor: parseFloat(item.score) / parseFloat(item.totalScore) >= 0.8 ? '#E5FBF8' :
                                                         parseFloat(item.score) / parseFloat(item.totalScore) >= 0.6 ? '#FBF4D9' : '#FCEDE9',
                                          color: parseFloat(item.score) / parseFloat(item.totalScore) >= 0.8 ? '#00806B' :
                                                parseFloat(item.score) / parseFloat(item.totalScore) >= 0.6 ? '#8A6D00' : '#A93A25',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          fontWeight: '500'
                                        }}>
                                          {Math.round((parseFloat(item.score) / parseFloat(item.totalScore)) * 100)}%
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* File attachment section */}
                              <div style={{
                                marginLeft: '36px',
                                marginTop: '10px',
                                padding: '12px',
                                backgroundColor: '#fff',
                                border: '1px solid #E6EDEC',
                                borderRadius: '6px'
                              }}>
                                <label style={{ fontSize: '14px', color: '#3A434E', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                                   파일 첨부 (선택):
                                </label>
                                <input
                                  type="file"
                                  onChange={(e) => updateStudentClassContentItem(activeStudentId, item.id, 'file', e.target.files[0])}
                                  disabled={item.source === 'bulk'}
                                  style={{
                                    fontSize: '13px',
                                    padding: '6px',
                                    border: '1px solid #E6EDEC',
                                    borderRadius: '4px',
                                    width: '100%',
                                    backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#F4F8F7',
                                    cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer'
                                  }}
                                />
                                {item.file && (
                                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#00D9C0' }}>
                                     {item.file.name}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Individual Homework - Editable UI */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <label style={{ fontWeight: '600', color: '#3A434E', fontSize: '16px' }}>
                          과제 ({settings.homeworkItems.length}개)
                        </label>
                        <button
                          type="button"
                          onClick={() => addStudentHomeworkItem(activeStudentId)}
                          style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            borderRadius: '5px',
                            backgroundColor: '#00D9C0',
                            border: 'none',
                            color: '#FFF',
                            cursor: 'pointer'
                          }}
                        >
                          + 과제 추가 (개별)
                        </button>
                      </div>

                      {settings.homeworkItems.length === 0 ? (
                        <div style={{
                          padding: '20px',
                          border: '1px dashed #E6EDEC',
                          borderRadius: '5px',
                          textAlign: 'center',
                          color: '#B9C1C9'
                        }}>
                          일괄 설정에서 추가하거나, 위의 "+ 과제 추가" 버튼으로 이 학생만의 과제를 추가하세요
                        </div>
                      ) : (
                        settings.homeworkItems.map((item, index) => (
                          <div key={item.id} style={{
                            marginBottom: '15px',
                            padding: '15px',
                            border: '1px solid #E6EDEC',
                            borderRadius: '5px',
                            backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#f9f9f9'
                          }}>
                            {/* Source badge */}
                            <div style={{ marginBottom: '10px' }}>
                              {item.source === 'bulk' ? (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  backgroundColor: '#E5FBF8',
                                  color: '#1976d2',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}>
                                  일괄 설정
                                </span>
                              ) : (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  backgroundColor: '#e8f5e9',
                                  color: '#388e3c',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}>
                                  개별 설정
                                </span>
                              )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <span style={{ fontWeight: '500', color: '#3A434E' }}>과제 {index + 1}</span>
                              {item.source === 'individual' && (
                                <button
                                  type="button"
                                  onClick={() => removeStudentHomeworkItem(activeStudentId, item.id)}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    borderRadius: '3px',
                                    backgroundColor: '#C2452D',
                                    border: 'none',
                                    color: '#FFF',
                                    cursor: 'pointer'
                                  }}
                                >
                                  삭제
                                </button>
                              )}
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#7A8490' }}>
                                과제 제목
                              </label>
                              <input
                                type="text"
                                placeholder="과제 제목을 입력하세요"
                                value={item.description}
                                onChange={(e) => updateStudentHomeworkItem(activeStudentId, item.id, 'description', e.target.value)}
                                disabled={item.source === 'bulk'}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #E6EDEC',
                                  borderRadius: '5px',
                                  fontSize: '14px',
                                  boxSizing: 'border-box',
                                  backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#FFF',
                                  cursor: item.source === 'bulk' ? 'not-allowed' : 'text'
                                }}
                              />
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#7A8490' }}>
                                과제 파일 선택 <span style={{ fontSize: '12px', color: '#B9C1C9' }}>(선택사항)</span>
                              </label>
                              <input
                                type="file"
                                onChange={(e) => updateStudentHomeworkItem(activeStudentId, item.id, 'file', e.target.files[0])}
                                disabled={item.source === 'bulk'}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #E6EDEC',
                                  borderRadius: '5px',
                                  fontSize: '14px',
                                  boxSizing: 'border-box',
                                  backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#FFF',
                                  cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer'
                                }}
                              />
                              {item.file && (
                                <div style={{ marginTop: '5px', color: '#7A8490', fontSize: '12px' }}>
                                  선택된 파일: {item.file.name}
                                </div>
                              )}
                            </div>

                            {/* End Date Section */}
                            <div style={{
                              marginTop: '10px',
                              padding: '12px',
                              backgroundColor: '#F4F8F7',
                              border: '1px solid #E5FBF8',
                              borderRadius: '5px'
                            }}>
                              <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer',
                                marginBottom: '8px'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={item.hasEndDate || false}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    updateStudentHomeworkItem(activeStudentId, item.id, 'hasEndDate', checked);

                                    if (checked && !item.endDate) {
                                      const defaultDate = new Date();
                                      defaultDate.setDate(defaultDate.getDate() + 3);
                                      const formatted = defaultDate.toISOString().slice(0, 10);
                                      updateStudentHomeworkItem(activeStudentId, item.id, 'endDate', formatted);
                                    }
                                  }}
                                  disabled={item.source === 'bulk'}
                                  style={{
                                    marginRight: '8px',
                                    cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer',
                                    width: '16px',
                                    height: '16px'
                                  }}
                                />
                                <span style={{ fontSize: '14px', color: '#3A434E', fontWeight: '500' }}>
                                  제출 마감일 설정
                                </span>
                              </label>

                              {item.hasEndDate && (
                                <div style={{ marginTop: '8px' }}>
                                  <label style={{
                                    display: 'block',
                                    marginBottom: '5px',
                                    fontSize: '13px',
                                    color: '#7A8490'
                                  }}>
                                    마감일시
                                  </label>
                                  <input
                                    type="date"
                                    value={item.endDate || ''}
                                    onChange={(e) => updateStudentHomeworkItem(activeStudentId, item.id, 'endDate', e.target.value)}
                                    disabled={item.source === 'bulk'}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      border: '1px solid #E5FBF8',
                                      borderRadius: '5px',
                                      fontSize: '14px',
                                      boxSizing: 'border-box',
                                      backgroundColor: item.source === 'bulk' ? '#F4F8F7' : '#FFF',
                                      cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                );
              })()}
              </div>
            </div>
          )}

          {/* Force Send Option */}
          <div style={{
            marginTop: '20px',
            marginBottom: '15px',
            padding: '11px 14px',
            backgroundColor: '#F4F8F7',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0E141B', letterSpacing: '-0.01em' }}>
                즉시 발송
              </div>
              <div style={{ fontSize: '0.74rem', color: '#7A8490', marginTop: '1px', lineHeight: 1.45 }}>
                대기열을 건너뛰고 바로 발송합니다. 오후 11시 이후에도 전송돼요.
              </div>
            </div>
            <EchoToggle
              checked={forceSend}
              onChange={() => setForceSend(!forceSend)}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '5px',
                backgroundColor: '#FFF',
                border: '1px solid #E6EDEC',
                color: '#7A8490',
                cursor: 'pointer'
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '5px',
                backgroundColor: loading ? '#B9C1C9' : '#0E141B',
                border: 'none',
                color: '#FFF',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '발송 중...' : '발송하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClassNoticeModal;
